import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Label,
  FormFeedback,
  Alert,
  Spinner,
  InputGroupText,
} from "reactstrap";
import { toast } from "react-toastify";
import newApi from "service/newApi";

const ZendeskConnectorModal = ({
  isOpen,
  toggle,
  clientId,
  connector = null,
  onSave,
}) => {
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [existingConnector, setExistingConnector] = useState(null);
  const [fetchingConnector, setFetchingConnector] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm();

  const isEditMode = !!connector || !!existingConnector;
  const currentConnector = connector || existingConnector;

  // Buscar conector existente para o cliente
  useEffect(() => {
    const fetchExistingConnector = async () => {
      if (isOpen && clientId && !connector) {
        setFetchingConnector(true);
        try {
          const response = await newApi.get(`/zendesk-connectors/${clientId}`);
          if (response.data && response.data.length > 0) {
            setExistingConnector(response.data[0]); // Pega o primeiro conector da lista
          }
        } catch (error) {
          // Se não encontrar conector (404), é normal, não é erro
          if (error.response?.status !== 404) {
            console.error("Erro ao buscar conector existente:", error);
          }
          setExistingConnector(null);
        } finally {
          setFetchingConnector(false);
        }
      }
    };

    fetchExistingConnector();
  }, [isOpen, clientId, connector]);

  useEffect(() => {
    if (isOpen) {
      setTestResult(null);

      if (currentConnector) {
        setValue("email", currentConnector.config?.email || "");
        setValue("subdomain", currentConnector.config?.subdomain || "");
        setValue("api_token", ""); // Não pré-preencher por segurança
        setValue("webhook_signing_key", ""); // Não pré-preencher por segurança
        setValue("ticket_id", ""); // Sempre limpar o ticket_id
      } else {
        reset();
      }
    } else {
      // Limpar estado quando modal for fechado
      setExistingConnector(null);
      setFetchingConnector(false);
    }
  }, [isOpen, currentConnector, setValue, reset]);

  // Remove auto-generation of URLs since they are no longer needed
  // const subdomain = watch("subdomain");
  // useEffect(() => {
  //   // Auto-generation logic removed
  // }, [subdomain, setValue, isEditMode]);

  const onSubmit = async (data) => {
    console.log("onSubmit chamado com data:", data);
    setLoading(true);
    try {
      let response;

      if (isEditMode) {
        const connectorId = currentConnector?.id;
        const configData = {
          subdomain: data.subdomain,
          email: data.email,
          api_token: data.api_token,
        };

        if (data.webhook_signing_key) {
          configData.webhook_signing_key = data.webhook_signing_key;
        }

        response = await newApi.put(`/zendesk-connectors/${connectorId}`, {
          config: configData,
        });
      } else {
        const configData = {
          subdomain: data.subdomain,
          email: data.email,
          api_token: data.api_token,
        };

        if (data.webhook_signing_key) {
          configData.webhook_signing_key = data.webhook_signing_key;
        }

        response = await newApi.post(`/zendesk-connectors`, {
          client_id: clientId,
          config: configData,
        });
      }

      toast.success(
        `Conector Zendesk ${isEditMode ? "atualizado" : "criado"} com sucesso!`,
      );
      onSave();
    } catch (error) {
      const message =
        error.response?.data?.message ||
        `Erro ao ${isEditMode ? "atualizar" : "criar"} conector Zendesk`;
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // Teste de conexão usando validação do formulário
  const handleTestConnection = async (formData) => {
    setTesting(true);
    setTestResult(null);
    try {
      const response = await newApi.post(
        `/zendesk-connectors/test-connection`,
        {
          subdomain: formData.subdomain,
          email: formData.email,
          api_token: formData.api_token,
          ticket_id: formData.ticket_id,
        },
      );
      setTestResult({
        success: true,
        message: response.data.message || "Conexão testada com sucesso!",
        details: response.data.details,
      });
    } catch (error) {
      setTestResult({
        success: false,
        message:
          error.response?.data?.message || "Erro ao testar conexão com Zendesk",
        details: error.response?.data?.details,
      });
    } finally {
      setTesting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>
        {fetchingConnector ? (
          <>
            <Spinner size="sm" className="me-2" />
            Verificando conector existente...
          </>
        ) : (
          `${isEditMode ? "Editar" : "Novo"} Conector Zendesk`
        )}
      </ModalHeader>
      <form
        onSubmit={handleSubmit(onSubmit, (errors) => {
          console.log("Erro de validação:", errors);
        })}
      >
        <ModalBody>
          <FormGroup>
            <Label for="subdomain">
              Subdomínio do Zendesk <span className="text-danger">*</span>
            </Label>
            <div className="d-flex">
              <input
                className={`form-control-alternative custom-input mr-2 ${
                  errors.subdomain ? "is-invalid" : ""
                }`}
                type="text"
                id="subdomain"
                placeholder="minha-empresa"
                {...register("subdomain", {
                  required: "Subdomínio é obrigatório",
                })}
                autoComplete="off"
              />
              <InputGroupText>.zendesk.com</InputGroupText>
            </div>
            {errors.subdomain && (
              <FormFeedback>{errors.subdomain?.message}</FormFeedback>
            )}
          </FormGroup>

          <FormGroup>
            <Label for="email">
              Email do usuário <span className="text-danger">*</span>
            </Label>
            <input
              className={`form-control-alternative custom-input ${
                errors.email ? "is-invalid" : ""
              }`}
              type="email"
              id="email"
              placeholder="usuario@empresa.com"
              {...register("email", {
                required: "Email é obrigatório",
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: "Email inválido",
                },
              })}
            />
            {errors.email && (
              <FormFeedback>{errors.email?.message}</FormFeedback>
            )}
          </FormGroup>

          <FormGroup>
            <Label for="api_token">
              Token da API <span className="text-danger">*</span>
            </Label>
            <input
              className={`form-control-alternative custom-input ${
                errors.api_token ? "is-invalid" : ""
              }`}
              type="password"
              id="api_token"
              placeholder="Cole aqui o token da API do Zendesk"
              autoComplete="new-password"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
              data-lpignore="true"
              data-form-type="other"
              {...register("api_token", {
                required: "Token da API é obrigatório",
              })}
            />
            {errors.api_token && (
              <FormFeedback>{errors.api_token?.message}</FormFeedback>
            )}
          </FormGroup>

          <FormGroup>
            <Label for="webhook_signing_key">
              Chave de Assinatura do Webhook{" "}
              {isEditMode && <span className="text-muted">(opcional)</span>}
            </Label>
            <input
              className={`form-control-alternative custom-input ${
                errors.webhook_signing_key ? "is-invalid" : ""
              }`}
              type="password"
              id="webhook_signing_key"
              placeholder="Chave de assinatura do webhook (opcional)"
              {...register("webhook_signing_key", {
                required: false, // Tornar opcional
              })}
            />
            {errors.webhook_signing_key && (
              <FormFeedback>{errors.webhook_signing_key?.message}</FormFeedback>
            )}
            <small className="form-text text-muted">
              Esta chave é fornecida pelo Zendesk ao configurar o webhook. Você
              pode cadastrá-la agora ou adicioná-la mais tarde.
            </small>
          </FormGroup>

          <FormGroup>
            <Label for="ticket_id">
              ID do Ticket (para teste) <span className="text-danger">*</span>
            </Label>
            <input
              className={`form-control-alternative custom-input ${
                errors.ticket_id ? "is-invalid" : ""
              }`}
              type="number"
              id="ticket_id"
              placeholder="Ex: 123"
              {...register("ticket_id", {
                required: "ID do ticket é obrigatório para o teste",
                min: {
                  value: 1,
                  message: "ID do ticket deve ser maior que 0",
                },
              })}
            />
            {errors.ticket_id && (
              <FormFeedback>{errors.ticket_id?.message}</FormFeedback>
            )}
            <small className="form-text text-muted">
              Digite o ID de um ticket existente no Zendesk para testar a
              conexão
            </small>
          </FormGroup>

          {/* Test Connection Button */}
          <div className="mt-4 pt-3 border-top">
            <Button
              type="button"
              color="info"
              disabled={testing}
              className="mb-3"
              onClick={handleSubmit(handleTestConnection)}
            >
              {testing ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Testando...
                </>
              ) : (
                <>
                  <i className="fas fa-plug me-2" />
                  Testar Conexão
                </>
              )}
            </Button>

            {/* Test Result */}
            {testResult && (
              <Alert color={testResult.success ? "success" : "danger"}>
                <h6>
                  <i
                    className={`fas fa-${
                      testResult.success ? "check-circle" : "exclamation-circle"
                    } me-2`}
                  />
                  Resultado do Teste
                </h6>
                <p className="mb-1">{testResult.message}</p>
                {testResult.details && (
                  <small className="text-muted">
                    Detalhes: {JSON.stringify(testResult.details)}
                  </small>
                )}
              </Alert>
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggle} disabled={loading}>
            Cancelar
          </Button>
          <Button color="primary" type="submit" disabled={loading}>
            {loading ? (
              <>
                <Spinner size="sm" className="me-2" />
                {isEditMode ? "Atualizando..." : "Criando..."}
              </>
            ) : (
              <>{isEditMode ? "Atualizar" : "Criar"} Conector</>
            )}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default ZendeskConnectorModal;
